// MessageActions — hover-action bar that floats above a message row.
//
// Declarative shape: the host passes a list of `MessageAction`s; this
// component arranges them in a 28px-tall toolbar with the brief's
// pinned gap, padding, and radius. The bar stays presentational — it
// renders buttons, applies a11y, and calls `action.onClick`. Opening
// the picker, showing a dialog, switching into inline-edit — all that
// lives at the row layer.
//
// Keyboard model: the toolbar follows the WAI-ARIA APG pattern.
// Exactly one button is in the tab sequence at a time (roving
// `tabIndex`); Left/Right move the active index, Home/End jump to
// ends. Popup-bearing buttons expose `aria-haspopup` / `aria-expanded`
// so assistive tech can announce the relationship.
//
// The bar is visually driven; the reveal-on-hover rule lives on the
// parent message row. This component renders the bar itself — the
// message row decides when to show it.

import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { getPortalViewportSize } from "~/lib/portal-positioning";
import { ICON_STROKE } from "~/lib/icon";
import { IconTooltip } from "~/components/IconTooltip";

const BAR_INSET = 8;

/** An action in the hover bar. Extensible on purpose — save, react,
 *  reply, edit, delete, more each build a `MessageAction`. */
export interface MessageAction {
  id: string;
  /** Rendered as the button's `aria-label` and the tooltip text. Must
   *  describe the *action*, not the state ("Save message", not "Saved"). */
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: CSSProperties }>;
  onClick: () => void;
  /** When `true` the glyph renders filled — used by toggle-style
   *  actions (Save is the first). */
  active?: boolean;
  disabled?: boolean;
  /**
   * Announce that this button opens a popup. Screen-reader users hear
   * the relationship; `aria-expanded` reports current open/closed.
   * Omitted for plain one-shot actions (Save, Reply, Edit).
   */
  hasPopup?: "dialog" | "menu";
  /** Required when `hasPopup` is set — reports popup state. */
  expanded?: boolean;
  /** Ref handed through to the rendered `<button>`. Popup-bearing
   *  actions use this to anchor the popover in the host layer. */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

export interface MessageActionsProps {
  actions: MessageAction[];
  /** The bar is reveal-on-hover on real rows but the hidden default
   *  breaks snapshot tests. Stories that want to render the bar
   *  unconditionally pass `forceVisible`. */
  forceVisible?: boolean;
  /** Reveal-on-hover rows pass their gutter visibility so the bar only
   *  clamps while shown: hidden rows skip the per-frame layout read and
   *  the bar is positioned the instant it appears, not on the next
   *  scroll. Pinned / forced-visible callers leave it defaulted to
   *  always-shown. */
  visible?: boolean;
}

export function MessageActions({
  actions,
  forceVisible = false,
  visible = true,
}: MessageActionsProps) {
  if (actions.length === 0) return null;
  return (
    <RovingToolbar count={actions.length} visible={visible}>
      <div
        role="toolbar"
        aria-label="Message actions"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--chat-space-1)",
          padding: "var(--chat-space-1)",
          borderRadius: "var(--chat-radius-sm)",
          background: "var(--chat-surface-overlay)",
          opacity: forceVisible ? 1 : undefined,
        }}
      >
        {actions.map((action) => (
          <ActionButton key={action.id} action={action} />
        ))}
      </div>
    </RovingToolbar>
  );
}

function ActionButton({ action }: { action: MessageAction }) {
  const Icon = action.icon;
  const color = action.active ? "var(--chat-fg-primary)" : "var(--chat-fg-secondary)";
  // The brief pins "24×24 icon button, 4px gap" on the hover action
  // bar. We use `aria-haspopup` when the action opens a popup so the
  // string matches the popup's own semantics — plain `true` (boolean
  // popup) fits MoreMenu + EmojiPicker, which deliberately aren't
  // claiming `role="menu"` / `role="dialog"`.
  return (
    <IconTooltip label={action.label} side="top">
      <button
        ref={action.buttonRef}
        type="button"
        data-action
        onClick={action.onClick}
        disabled={action.disabled}
        aria-label={action.label}
        aria-pressed={action.hasPopup ? undefined : action.active === true}
        aria-haspopup={action.hasPopup ? true : undefined}
        aria-expanded={action.hasPopup ? action.expanded : undefined}
        className="chat-button"
        style={{
          width: "var(--chat-action-button-size)",
          height: "var(--chat-action-button-size)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: "transparent",
          color,
          cursor: action.disabled ? "not-allowed" : "pointer",
          padding: 0,
          opacity: action.disabled ? 0.5 : 1,
        }}
      >
        <Icon
          size={14}
          strokeWidth={ICON_STROKE}
          style={{
            color,
            fill: action.active ? color : "none",
          }}
        />
      </button>
    </IconTooltip>
  );
}

function clipBand(
  el: HTMLElement,
  portalRoot: Parameters<typeof getPortalViewportSize>[0],
): { top: number; bottom: number } {
  for (
    let node: HTMLElement | null = el;
    node && node !== document.body;
    node = node.parentElement
  ) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === "auto" || overflow === "scroll") {
      const rect = node.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom };
    }
  }
  const surface = getPortalViewportSize(portalRoot);
  return { top: surface.offsetTop, bottom: surface.offsetTop + surface.height };
}

/**
 * Roving-tabindex container. Walks the child buttons, sets
 * `tabIndex={-1}` on all but the active one, and intercepts arrow /
 * Home / End keys to move the active index. This is the behaviour the
 * `role="toolbar"` ARIA pattern promises; implementing it here keeps
 * the child buttons ordinary — they don't need to know they're inside
 * a toolbar.
 *
 * `active` is kept in sync with real focus via `onFocus` — clicking a
 * button or tabbing into it moves the roving index so the next
 * arrow-key press steps from the focused button, not from the last
 * position the keyboard left. Without this, mouse and keyboard drift
 * apart the moment you click the third button.
 */

function RovingToolbar({
  children,
  count,
  visible,
}: {
  children: React.ReactNode;
  count: number;
  visible: boolean;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const portalRoot = usePortalRoot();
  const [shift, setShift] = useState(0);
  const shiftRef = useRef(0);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || !visible) {
      shiftRef.current = 0;
      setShift(0);
      return;
    }
    const recompute = () => {
      // getBoundingClientRect carries our own translateY; subtract the
      // applied shift so we measure the anchor, never the shifted bar.
      // Measuring the shifted bar makes each clamp read itself back and
      // flicker off-screen on alternating scroll events.
      const rect = el.getBoundingClientRect();
      const anchorTop = rect.top - shiftRef.current;
      const anchorBottom = rect.bottom - shiftRef.current;
      const band = clipBand(el, portalRoot);
      let next = 0;
      if (anchorTop < band.top) {
        next = band.top - anchorTop + BAR_INSET;
      } else if (anchorBottom > band.bottom) {
        next = band.bottom - anchorBottom - BAR_INSET;
      }
      if (next !== shiftRef.current) {
        shiftRef.current = next;
        setShift(next);
      }
    };
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [portalRoot, visible]);

  // Clamp `active` if the action list shrinks (e.g. edit/delete
  // disappear when the sender flips). Without this the chosen index
  // can point past the end of the button list and no button ends up
  // tabbable.
  const clamped = count === 0 ? 0 : Math.min(active, count - 1);

  // Apply the current tabIndex state to the DOM buttons. `[clamped,
  // count]` ensures the effect only runs when the index or count
  // actually changes — running every render would rewrite DOM state
  // on every unrelated parent render.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>("button[data-action]"));
    buttons.forEach((btn, i) => {
      btn.tabIndex = i === clamped ? 0 : -1;
    });
  }, [clamped, count]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
      if (count === 0) return;
      e.preventDefault();
      const next =
        e.key === "ArrowLeft"
          ? (clamped - 1 + count) % count
          : e.key === "ArrowRight"
            ? (clamped + 1) % count
            : e.key === "Home"
              ? 0
              : count - 1;
      setActive(next);
      const buttons = rootRef.current?.querySelectorAll<HTMLButtonElement>("button[data-action]");
      buttons?.[next]?.focus();
    },
    [clamped, count],
  );

  const onFocus = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    if (!root) return;
    const focused = e.target;
    // Guard the cast: `onFocus` here receives a `FocusEvent<HTMLDivElement>`
    // because the handler is wired on the wrapping `<div>`, but the
    // actual focused element is one of the toolbar buttons. We only
    // promote the type after the `instanceof` check confirms it.
    if (!(focused instanceof HTMLButtonElement)) return;
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>("button[data-action]"));
    const idx = buttons.indexOf(focused);
    if (idx === -1) return;
    setActive(idx);
  }, []);

  return (
    <div
      ref={rootRef}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      style={{
        display: "inline-block",
        ...(shift ? { transform: `translateY(${shift}px)` } : null),
      }}
    >
      {children}
    </div>
  );
}

/* ── Action builders ──────────────────────────────────────────────── */

// Re-exported from the individual action modules so consumers import
// one place for "the actions available on a message row". New actions
// (reactions, reply, thread, edit, delete, more) register themselves
// through this file.

export { buildSaveAction } from "~/components/message-actions/save";
export { buildPinAction } from "~/components/message-actions/pin";
export { buildReactAction } from "~/components/message-actions/react";
export { buildReplyAction } from "~/components/message-actions/reply";
export { buildQuoteAction } from "~/components/message-actions/quote";
export { buildEditAction } from "~/components/message-actions/edit";
export { buildDeleteAction } from "~/components/message-actions/delete";
export { buildMoreAction } from "~/components/message-actions/more";
